import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';
import { 
  Target, 
  Heart, 
  Zap, 
  Globe,
  Users,
  Shield,
  ArrowRight
} from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_mercamove/artifacts/ch8knfdf_Move_Logo_wHT.png" 
                alt="Mercapoli Move"
                className="h-8"
              />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-[#00BFA6]">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6 py-2 text-sm">
                  Registrarse
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-[#00BFA6]/5 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-['Outfit'] leading-tight">
            Sobre <span className="text-[#00BFA6]">Mercapoli Move</span>
          </h1>
          <p className="text-xl text-slate-600 mt-6 max-w-3xl mx-auto leading-relaxed">
            Mercapoli Move es una plataforma tecnológica de movilidad y logística creada para 
            conectar personas, vehículos y servicios en un solo ecosistema digital.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] mb-6">
                Nuestra Misión
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Simplificar la forma en que el mundo se mueve, transporta y paga, utilizando 
                tecnología escalable, segura y global.
              </p>
              <p className="text-slate-600 mt-4">
                Creemos que la movilidad debe ser accesible, eficiente y conectada. Por eso 
                construimos una super-app que integra todos los servicios de transporte y 
                logística que necesitas en tu día a día.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#00BFA6]/5 p-6 rounded-2xl">
                <Globe className="w-10 h-10 text-[#00BFA6] mb-3" />
                <p className="font-bold text-slate-800">Global</p>
                <p className="text-slate-500 text-sm">Presencia multi-país</p>
              </div>
              <div className="bg-[#FFD166]/5 p-6 rounded-2xl">
                <Users className="w-10 h-10 text-[#FFD166] mb-3" />
                <p className="font-bold text-slate-800">+10,000</p>
                <p className="text-slate-500 text-sm">Usuarios activos</p>
              </div>
              <div className="bg-[#00887A]/5 p-6 rounded-2xl">
                <Shield className="w-10 h-10 text-[#00887A] mb-3" />
                <p className="font-bold text-slate-800">Seguro</p>
                <p className="text-slate-500 text-sm">Verificación total</p>
              </div>
              <div className="bg-[#00BFA6]/5 p-6 rounded-2xl">
                <Zap className="w-10 h-10 text-[#00BFA6] mb-3" />
                <p className="font-bold text-slate-800">Rápido</p>
                <p className="text-slate-500 text-sm">Servicio inmediato</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 font-['Outfit'] text-center mb-12">
            Nuestros Valores
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#00BFA6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Innovación</h3>
              <p className="text-slate-600 text-sm">Tecnología de vanguardia para soluciones modernas.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#FFD166]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#FFD166]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Confianza</h3>
              <p className="text-slate-600 text-sm">Relaciones transparentes con todos nuestros usuarios.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#00887A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-[#00887A]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Eficiencia</h3>
              <p className="text-slate-600 text-sm">Optimizamos cada proceso para ahorrarte tiempo.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#00BFA6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-[#00BFA6]" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Inclusión</h3>
              <p className="text-slate-600 text-sm">Servicios financieros accesibles para todos.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#00BFA6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
            Únete a la revolución de la movilidad
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Forma parte del ecosistema Mercapoli Move.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#00BFA6] hover:bg-white/90 h-14 px-8 text-lg rounded-full">
              Comenzar Ahora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
